import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { CircularProgress, Typography } from '@mui/material';
import { FlatPaginationComp } from 'shared/components/Pagination';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { formattingDate } from 'shared/helpers/FormatDate';
import EnhancedTableHead from './DashboardTableHead';

import { useMeasure } from 'react-use';
import mergeRefs from 'merge-refs';
import { useSelector } from 'react-redux';

export default function EnhancedTable({
    handleChangeSortBy,
    handleChangeSearchBy,
    handleChangePacketStatus,
    handleChangePacketType,
    formikRef,
    updateSearch,
    setSearchValues,
    state,
    isNetworkError,
    setState,
}: VirtualizedTable): React.ReactElement {
    const innerContainerRef = React.useRef<HTMLTableElement>(null);
    const role = useSelector((state: any) => state.authState.role);
    const [order] = React.useState<'asc' | 'desc'>('desc');
    const [orderBy] = React.useState<keyof any>('binNumber');
    const [tableRef, { width }] = useMeasure<HTMLTableElement>();
    const innerPaginationComp = React.useRef<HTMLDivElement>(null);

    const actionOnTableHeadToRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof any,
        search?: any
    ): void => {
        switch (property) {
            case 'packetType':
                handleChangePacketType(search);
                break;
            case 'packetStatus':
                handleChangePacketStatus(search);
                break;
            case 'completedDate':
                handleChangeSortBy(
                    search === 'Newest First'
                        ? 'dateCompleted'
                        : 'desDateCompleted'
                );
                break;
            case 'uploadedDate':
                handleChangeSortBy(
                    search === 'Newest First' ? 'dateOfUpload' : 'oldestToNew'
                );
                break;
            case 'SLADate':
                handleChangeSortBy(
                    search === 'Newest First'
                        ? 'desDaysLeftToSLA'
                        : 'daysLeftToSLA'
                );
                break;
            case 'escalatedDate':
                handleChangeSortBy(
                    search === 'Newest First'
                        ? 'escalatedAge'
                        : 'desEscalatedAge'
                );
                break;
            case 'clientName':
            case 'binNumber':
            case 'loanNumber':
            case 'operatorId':
                handleChangeSearchBy(property, search);
                break;
            default:
                break;
        }
    };

    /**
     * Renders the class name based on the literal text to create packet status class name.
     *
     * @param {string} text - The literal text, e.g., 'Completed', 'Verified', 'Escalated', etc.
     * @returns {string} The corresponding class name.
     */
    const renderOnLiteralToCreatePacketStatusClassName = (
        text: string
    ): string => {
        let className = 'defaultStatus';
        switch (text) {
            case 'Completed':
                className = 'completed';
                break;
            case 'Verified':
                className = 'verified';
                break;
            case 'Escalated':
                className = 'escalated';
                break;
            case 'Review Required':
                className = 'review-required';
                break;
            // case 'Sent for rework':
            //     className = 'sentrework';
            //     break;
            case 'Open By another Member':
                className = 'openbyanother';
                break;
            case 'Processing Verification':
                className = 'processingqy';
                break;
            default:
                break;
        }
        return className;
    };

    /**
     * Renders the class name based on the literal text to create packet type class name.
     *
     * @param {string} text - The literal text, e.g., 'Digital', 'Hybrid', etc.
     * @returns {string} The corresponding class name.
     */
    const renderOnLiteralToCreatePacketTypeClassName = (
        text: string
    ): string => {
        let className = 'defaultType';
        switch (text) {
            case 'Digital':
                className = 'digitalType';
                break;
            case 'Hybrid':
                className = 'hybridType';
                break;
            default:
                break;
        }
        return className;
    };

    /**
     * Handles pagination page change.
     * @param {object} event - The event object.
     * @param {number} newPage - The Newest First page number.
     * @returns {void}
     */
    const onChangePagination = (
        event: React.ChangeEvent<unknown> | null,
        newPage: number
    ): void => {
        setState((prevState: any) => ({
            ...prevState,
            page: newPage - 1,
            isLoading: true,
        }));
    };

    React.useLayoutEffect(() => {
        const { current: paginationContainer } = innerPaginationComp;
        if (paginationContainer) {
            paginationContainer.style.width = `${width}px`;
        }
    }, [width, state.data.length]);

    const getOperatorName = React.useCallback((operatorName: any) => {
        const lastName =
            typeof operatorName === 'object' && operatorName !== null
                ? operatorName.last
                : '';
        const firstName =
            typeof operatorName === 'object' && operatorName !== null
                ? operatorName.first
                : '';
        const nameOperator = String(firstName).substring(0, 1).toUpperCase();

        return typeof operatorName === 'object' && operatorName !== null
            ? `${lastName}, ${nameOperator}`
            : '-';
    }, []);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleChangeRowsPerPage = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setState({
                ...state,
                rowsPerPage: parseInt(event.target.value, 10),
                page: 0,
                isLoading: true,
            });
        },
        [setState, state]
    );

    return (
        <Box className="wrapper-default-table">
            <Paper elevation={0} className="default-table">
                <TableContainer
                    className="default-table-container"
                    sx={{ padding: state.isDrawerOpen ? '' : '0 40px' }}
                >
                    <Typography variant="h3">Dashboard</Typography>
                    <Table
                        aria-labelledby="tableTitle"
                        size="medium"
                        ref={mergeRefs(innerContainerRef, tableRef)}
                    >
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={actionOnTableHeadToRequestSort}
                            formikRef={formikRef}
                            updateSearch={updateSearch}
                            searchValues={state.searchValues}
                            setSearchValues={setSearchValues}
                        />
                        {state.isLoading && (
                            <TableBody className="manage-client-relative">
                                <Box minHeight={400} position="relative">
                                    <Box className="align-loader-table">
                                        <CircularProgress
                                            className="mr-2 margin-loader"
                                            size={54}
                                            color="inherit"
                                        />
                                    </Box>
                                </Box>
                            </TableBody>
                        )}
                        {!state.isLoading && state.data.length < 1 && (
                            <TableBody className="manage-client-relative">
                                <Box className="align-loader-placeholder">
                                    <EmptyPlaceholder
                                        message={
                                            isNetworkError
                                                ? 'No internet connection, Please try again!'
                                                : 'There are no packet records yet'
                                        }
                                    />
                                </Box>
                            </TableBody>
                        )}
                        {!state.isLoading && state.data.length > 0 && (
                            <TableBody className="default-table-body">
                                {state.data.map((row, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}-${row.bin}-${state.page}`;

                                    return (
                                        <TableRow key={`${row.bin} - ${index}`}>
                                            <TableCell
                                                component="th"
                                                id={labelId}
                                                className={renderOnLiteralToCreatePacketStatusClassName(
                                                    row.packetStatus
                                                )}
                                                scope="row"
                                            >
                                                {row.packetStatus}
                                            </TableCell>
                                            <TableCell align="left">
                                                <Typography className="different-days">
                                                    {` (${row.createdAtDifference} days ago)`}
                                                </Typography>
                                                <Typography className="datetime">
                                                    {`${formattingDate(
                                                        row.uploadedDate
                                                    )}`}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                align={
                                                    row.SLADate
                                                        ? 'left'
                                                        : 'center'
                                                }
                                            >
                                                {row.slaDateDifference > 0 && (
                                                    <Typography className="different-days sla">
                                                        {` (${row.slaDateDifference} days left)`}
                                                    </Typography>
                                                )}
                                                <Typography className="datetime">
                                                    {`${formattingDate(
                                                        row.SLADate
                                                    )}`}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className="clientname"
                                            >
                                                {row.clientName}
                                            </TableCell>
                                            <TableCell align="left">
                                                {row.bin}
                                            </TableCell>
                                            <TableCell align="left">
                                                {row.loanNumber}
                                            </TableCell>
                                            <TableCell align="left">
                                                {getOperatorName(
                                                    row.operatorName
                                                )}
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className={renderOnLiteralToCreatePacketTypeClassName(
                                                    row.packetType
                                                )}
                                            >
                                                {row.packetType}
                                            </TableCell>
                                            <TableCell align="center">
                                                {row.packetStatus ===
                                                'Completed' ? (
                                                    <>
                                                        <Typography className="different-days">
                                                            {`(${row.completedDateDifference} days ago)`}
                                                        </Typography>
                                                        <Typography className="datetime">
                                                            {`${formattingDate(
                                                                row.completedDate
                                                            )}`}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <>-</>
                                                )}
                                            </TableCell>
                                            {role !== 'operator' && (
                                                <TableCell
                                                    align={
                                                        row.packetStatus ===
                                                        'Escalated'
                                                            ? 'left'
                                                            : 'center'
                                                    }
                                                >
                                                    {row.packetStatus ===
                                                    'Escalated' ? (
                                                        <>
                                                            <Typography className="different-days">
                                                                {`(${row.updatedAtDifference} days ago)`}
                                                            </Typography>
                                                            <Typography className="datetime">
                                                                {`${formattingDate(
                                                                    row.escalatedDate
                                                                )}`}
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <>-</>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        )}
                    </Table>
                    <FlatPaginationComp
                        hidden={
                            (!state.isLoading && state.data.length >= 1) ===
                            false
                        }
                        containerClassName="dashboard"
                        className="table-default-pagination"
                        innerRef={innerPaginationComp}
                        data={state.totalRows}
                        page={state.page}
                        count={Math.ceil(state.totalRows / state.rowsPerPage)}
                        onPageChange={onChangePagination}
                        rowsPerPage={state.rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        onChange={onChangePagination}
                        popupPrevNext
                    />
                </TableContainer>
            </Paper>
        </Box>
    );
}
